import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Trash2, RefreshCw, MoreHorizontal, ExternalLink, Star, Archive, ArchiveRestore, Users, ChevronDown, ChevronRight,
  UserPlus, Check, Lock, LockOpen, Copy,
} from "lucide-react";
import { toTitleCasePt } from "@/lib/format";
import { BadgeParticular } from "@/components/Particular";
import AutosTag from "@/components/AutosTag";
import AcompanhamentoProcesso from "@/components/AcompanhamentoProcesso";
import { SEL_CINZA } from "@/lib/selecao";
import {
  acoesDoProcesso, brDateFull, etiqueta, nextCta, partiesRaw, quickReview, relTime,
  syncMoment, TOM_CLS, tribunalLabel,
} from "@/lib/processosUtil";

// Lote 165 — uma linha da lista de Processos: compacta, que expande para os detalhes.
// Computador: colunas Processo/Cliente · Próxima ação · Responsável (só no escritório) · controles.
// Celular: o mesmo bloco vira cartão (título, próxima ação, responsável), sem rolagem lateral.
// Na linha só cabem: o título (abre o processo), o texto da próxima ação (1 toque), ★, ⋯ e a seta de expandir.
// Os botões contextuais (Ver processo, Já revisado, Vincular…) ficam na expansão.
const ctl = "inline-flex items-center justify-center h-9 w-9 rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400";

export default function ProcessoLinha({
  p, aberta, onToggle, nomeCliente, parados, noEscritorio, meuId, selMode, selecionado, minhas,
  clients, oneBusy, revBusy, a,
}) {
  const cli = nomeCliente(p.client_id);
  const parties = partiesRaw(p);
  const apelido = (p.apelido || "").trim();
  const title = cli || apelido || parties || p.numero;
  const sub = cli ? (parties || apelido) : (title === parties || title === p.numero ? "" : parties);
  const onde = [tribunalLabel(p.tribunal_alias), p.comarca && toTitleCasePt(p.comarca)].filter(Boolean).join(" · ");
  const et = etiqueta(p, parados);
  const cta = nextCta(p, parados);
  const acs = acoesDoProcesso(p);
  const prim = acs[0];
  const ctaLink = cta?.kind === "link" ? cta : null;
  const ctaExtra = cta && !ctaLink ? cta : null;     // atualizar / vincular / reativar → dentro da expansão
  const qr = (p.pendencias > 0 || p.status_kind === "pendencia") ? quickReview(p) : null;
  const showVincularExtra = !p.client_id && !p.arquivado && cta && cta.kind !== "vincular";
  const abrirProcesso = () => a.abrir(p);
  const colCtl = noEscritorio ? "md:col-start-4" : "md:col-start-3";
  const cols = noEscritorio
    ? "md:grid-cols-[minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_auto]"
    : "md:grid-cols-[minmax(0,1.7fr)_minmax(0,1.2fr)_auto]";

  const copiar = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(p.numero); toast.success("Número copiado"); }
    catch (err) { toast.error("Não consegui copiar. Selecione o número e copie."); }
  };

  const clickLinha = () => {
    if (selMode) a.toggleSel(p);
    else onToggle();
  };

  return (
    <li>
      <div
        data-testid={`processo-row-${p.processo_id}`}
        data-aberta={aberta ? "1" : "0"}
        onClick={clickLinha}
        className={`rounded-lg border bg-white transition-colors ${aberta ? "border-stone-300 shadow-sm" : "border-stone-200 hover:border-stone-300"} ${p.arquivado ? "bg-stone-50" : ""} ${selMode && !minhas(p) ? "opacity-50 cursor-not-allowed" : selMode ? "cursor-pointer" : "md:cursor-pointer"} ${selMode && selecionado ? "ring-2 ring-stone-500" : ""}`}
      >
        <div className={`grid grid-cols-[minmax(0,1fr)_auto] ${cols} gap-x-5 gap-y-3 items-start md:items-center px-4 py-3.5`}>
          {/* Processo / Cliente */}
          <div className="col-start-1 row-start-1 min-w-0 flex items-start gap-3">
            {selMode && (
              <span
                data-testid={`sel-check-${p.processo_id}`}
                data-checked={selecionado ? "1" : "0"}
                aria-hidden="true"
                title={minhas(p) ? "" : "Processo da conta de outra pessoa — só ela decide"}
                className={`mt-1 inline-flex items-center justify-center w-5 h-5 shrink-0 rounded border ${!minhas(p) ? "border-stone-200 bg-stone-100" : selecionado ? SEL_CINZA : "border-stone-400 bg-white"}`}
              >
                {selecionado && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <button
                  type="button"
                  data-testid={`processo-abrir-${p.processo_id}`}
                  onClick={(e) => { e.stopPropagation(); if (selMode) a.toggleSel(p); else abrirProcesso(); }}
                  className="font-display text-base leading-snug font-semibold text-stone-900 text-left break-words hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 rounded-sm"
                >
                  {title}
                </button>
                {(p.autos_total > 1 || (p.autos && p.autos !== "principal")) && (
                  <AutosTag autos={p.autos} nome={p.autos_nome} testid={`processo-autos-${p.processo_id}`} />
                )}
                {p.particular && <BadgeParticular testid={`processo-particular-${p.processo_id}`} />}
                {p.a_classificar > 0 && (
                  <span data-testid={`processo-fila-${p.processo_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-800">
                    {p.a_classificar} a classificar
                  </span>
                )}
                {et && (
                  <span data-testid={`processo-etiqueta-${p.processo_id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${TOM_CLS[et.tom]}`}>
                    {et.texto}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-stone-500 min-w-0">
                <span className="tabular-nums whitespace-nowrap">{p.numero}</span>
                <button type="button" data-testid={`processo-copiar-${p.processo_id}`} aria-label="Copiar número do processo" title="Copiar número" onClick={copiar}
                  className="inline-flex items-center justify-center h-6 w-6 shrink-0 rounded text-stone-400 hover:bg-stone-100 hover:text-stone-700">
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
                {onde && <span className="break-words">· {onde}</span>}
              </div>
              {!p.client_id ? (
                <p className="mt-0.5 text-xs italic text-stone-500" data-testid={`processo-sem-cliente-${p.processo_id}`}>Sem cliente vinculado</p>
              ) : sub ? (
                <p className="mt-0.5 text-xs text-stone-600 break-words">{sub}</p>
              ) : null}
            </div>
          </div>

          {/* Próxima ação */}
          <div className="col-span-2 row-start-2 md:col-span-1 md:col-start-2 md:row-start-1 min-w-0" data-testid={`processo-acao-${p.processo_id}`}>
            {p.arquivado ? (
              <p className="text-sm text-stone-500">Consulta pausada</p>
            ) : ctaLink || prim ? (
              <>
                {ctaLink ? (
                  <button
                    type="button"
                    data-testid={`processo-cta-${p.processo_id}`}
                    data-cta={p.next_action?.kind}
                    onClick={(e) => { e.stopPropagation(); a.runCta(p, ctaLink); }}
                    className="text-sm font-semibold text-stone-900 underline decoration-stone-300 underline-offset-4 hover:decoration-stone-700 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 rounded-sm"
                  >
                    {ctaLink.label}
                  </button>
                ) : (
                  <p className="text-sm font-semibold text-stone-900">{prim.label}</p>
                )}
                {prim && <p className="text-xs text-stone-500 mt-0.5 break-words">{prim.motivo}</p>}
                {acs.length > 1 && (
                  <p className="text-xs font-medium text-stone-600 mt-0.5" data-testid={`processo-mais-${p.processo_id}`}>+{acs.length - 1} {acs.length - 1 === 1 ? "ação" : "ações"}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-stone-500">Nenhuma providência cadastrada</p>
            )}
          </div>

          {/* Responsável */}
          {noEscritorio && (
            <div className="col-span-2 row-start-3 md:col-span-1 md:col-start-3 md:row-start-1 min-w-0 text-sm" data-testid={`processo-resp-${p.processo_id}`}>
              {p.responsavel_nome ? (
                <>
                  <span className="md:hidden text-xs text-stone-500">Responsável: </span>
                  <span className="font-medium text-stone-800">{p.responsavel_nome}</span>{p.responsavel_ex ? <span className="text-stone-500"> (saiu)</span> : null}
                  {p.dono_id !== meuId && p.dono_nome && p.dono_id !== p.responsavel_id ? <span className="block text-xs text-stone-500">conta de {p.dono_nome}</span> : null}
                  {p.tambem_em?.length > 0 && <span className="block text-xs text-stone-500" data-testid={`processo-tambem-${p.processo_id}`}>também na conta de {p.tambem_em.map((t) => t.nome).join(", ")}</span>}
                </>
              ) : (
                <span className="text-stone-500">Sem responsável</span>
              )}
            </div>
          )}

          {/* Controles */}
          <div className={`col-start-2 row-start-1 ${colCtl} flex items-center -mr-1.5 ${selMode ? "invisible" : ""}`}>
            <button
              type="button"
              data-testid={`processo-star-${p.processo_id}`}
              aria-label={p.prioritario ? "Remover dos prioritários" : "Marcar como prioritário"}
              aria-pressed={!!p.prioritario}
              title={p.prioritario ? "Prioritário — toque para remover" : "Marcar como prioritário"}
              onClick={(e) => { e.stopPropagation(); a.toggleStar(p); }}
              className={ctl}
            >
              <Star className={`w-[18px] h-[18px] ${p.prioritario ? "fill-gold-500 text-gold-500" : ""}`} strokeWidth={1.75} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label="Mais ações" data-testid={`processo-menu-${p.processo_id}`} onClick={(e) => e.stopPropagation()} className={ctl}>
                  <MoreHorizontal className="w-[18px] h-[18px]" strokeWidth={1.75} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onSelect={abrirProcesso} className="gap-2">
                  <ExternalLink className="w-4 h-4" strokeWidth={1.75} /> Abrir processo
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`sync-processo-${p.processo_id}`} disabled={oneBusy === p.processo_id} onSelect={() => a.syncOne(p.processo_id)} className="gap-2">
                  <RefreshCw className="w-4 h-4" strokeWidth={1.75} /> {oneBusy === p.processo_id ? "Atualizando…" : "Atualizar andamentos"}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`vincular-menu-${p.processo_id}`} onSelect={() => a.setVincular(p)} className="gap-2">
                  <UserPlus className="w-4 h-4" strokeWidth={1.75} /> {p.client_id ? "Trocar cliente" : "Vincular cliente"}
                </DropdownMenuItem>
                {minhas(p) && (
                  <DropdownMenuItem data-testid={`copia-menu-${p.processo_id}`} onSelect={() => a.compartilhar(p)} className="gap-2">
                    <Users className="w-4 h-4" strokeWidth={1.75} /> Compartilhar com parceiro
                  </DropdownMenuItem>
                )}
                {noEscritorio && minhas(p) && (
                  <DropdownMenuItem data-testid={`particular-menu-${p.processo_id}`} onSelect={() => a.alternarParticular(p)} className="gap-2">
                    {p.particular ? <LockOpen className="w-4 h-4" strokeWidth={1.75} /> : <Lock className="w-4 h-4" strokeWidth={1.75} />}
                    {p.particular ? "Voltar a compartilhar" : "Marcar como particular"}
                  </DropdownMenuItem>
                )}
                {p.arquivado ? (
                  <DropdownMenuItem data-testid={`reativar-processo-${p.processo_id}`} onSelect={() => a.setArquivado(p, false)} className="gap-2">
                    <ArchiveRestore className="w-4 h-4" strokeWidth={1.75} /> Reativar
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem data-testid={`arquivar-processo-${p.processo_id}`} onSelect={() => a.setArquivado(p, true)} className="gap-2">
                      <Archive className="w-4 h-4" strokeWidth={1.75} /> Arquivar (pausa a consulta)
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`extinto-processo-${p.processo_id}`} onSelect={() => a.setArquivado(p, true, false, "extinto")} className="gap-2">
                      <Archive className="w-4 h-4" strokeWidth={1.75} /> Marcar como extinto
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem data-testid={`delete-processo-${p.processo_id}`} onSelect={() => a.del(p.processo_id)} className="gap-2 text-rose-brand focus:text-rose-700 focus:bg-rose-100">
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} /> Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              data-testid={`processo-expandir-${p.processo_id}`}
              aria-expanded={aberta}
              aria-label={aberta ? "Recolher detalhes" : "Ver detalhes"}
              title={aberta ? "Recolher" : "Ver detalhes"}
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              className={ctl}
            >
              {aberta ? <ChevronDown className="w-[18px] h-[18px]" strokeWidth={2} /> : <ChevronRight className="w-[18px] h-[18px]" strokeWidth={2} />}
            </button>
          </div>
        </div>

        {aberta && !selMode && (
          <div
            data-testid={`processo-detalhe-${p.processo_id}`}
            onClick={(e) => e.stopPropagation()}
            className="border-t border-stone-200 bg-stone-50/60 px-4 py-4 space-y-4 rounded-b-lg cursor-default"
          >
            <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              <div data-testid={`det-ultima-${p.processo_id}`}>
                <dt className="text-xs font-semibold text-stone-500">Última movimentação capturada</dt>
                <dd className="text-stone-800 mt-0.5 break-words">
                  {p.last_movement_at ? (
                    <>
                      {brDateFull(p.last_movement_date || p.last_movement_at)}
                      {p.last_movement_text ? <span className="block text-stone-600">{p.last_movement_text}</span> : null}
                      {Date.now() - new Date(p.last_movement_at).getTime() < 48 * 3600 * 1000 && <span className="block text-xs text-stone-500">Nova {relTime(p.last_movement_at)}</span>}
                    </>
                  ) : "Ainda sem movimentações capturadas"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-stone-500">Órgão julgador</dt>
                <dd className="text-stone-800 mt-0.5 break-words">
                  {[p.vara && toTitleCasePt(p.vara), p.comarca && toTitleCasePt(p.comarca), tribunalLabel(p.tribunal_alias)].filter(Boolean).join(" · ") || "Não informado"}
                </dd>
              </div>
              <div data-testid={`det-monitoramento-${p.processo_id}`}>
                <dt className="text-xs font-semibold text-stone-500">Monitoramento</dt>
                <dd className="text-stone-800 mt-0.5">
                  {p.arquivado ? "Consulta pausada" : "Ativo"}
                  <span className="block text-xs text-stone-500">
                    {p.datajud_last_ok_at ? `Última consulta bem-sucedida: ${syncMoment(p.datajud_last_ok_at)}` : "Nenhuma consulta concluída ainda"}
                  </span>
                </dd>
              </div>
              <div data-testid={`det-providencias-${p.processo_id}`}>
                <dt className="text-xs font-semibold text-stone-500">Demais providências</dt>
                <dd className="text-stone-800 mt-0.5">
                  {acs.length > 1 ? (
                    <ul className="space-y-0.5">{acs.slice(1).map((x) => <li key={x.kind}>{x.motivo}</li>)}</ul>
                  ) : "Nenhuma além da próxima ação"}
                </dd>
              </div>
              {noEscritorio && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold text-stone-500">Responsáveis</dt>
                  <dd className="text-stone-800 mt-0.5">
                    {p.responsavel_nome ? `${p.responsavel_nome}${p.responsavel_ex ? " (saiu)" : ""}` : "Sem responsável"}
                    {p.dono_nome && p.dono_id !== p.responsavel_id ? <span className="text-stone-500"> · processo na conta de {p.dono_nome}</span> : null}
                  </dd>
                </div>
              )}
            </dl>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" data-testid={`processo-ver-${p.processo_id}`} onClick={abrirProcesso} className="bg-stone-700 hover:bg-stone-800 text-white gap-1.5 h-9">
                <ExternalLink className="w-4 h-4" strokeWidth={1.75} /> Ver processo
              </Button>
              {ctaExtra && (
                <Button
                  type="button" size="sm" variant="outline" data-testid={`processo-cta-${p.processo_id}`} data-cta={ctaExtra.kind}
                  disabled={ctaExtra.kind === "atualizar" && oneBusy === p.processo_id}
                  onClick={() => a.runCta(p, ctaExtra)} className="border-stone-300 bg-white h-9"
                >
                  {ctaExtra.kind === "atualizar" && oneBusy === p.processo_id ? "Atualizando…" : ctaExtra.label}
                </Button>
              )}
              {qr && (
                <Button
                  type="button" size="sm" data-testid={`processo-revisado-${p.processo_id}`} data-mode={qr.mode} disabled={revBusy}
                  onClick={() => a.runQuick(p, qr)}
                  className={`h-9 ${qr.mode === "ata" ? "border border-stone-300 bg-white text-stone-800 hover:bg-stone-50" : "bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"}`}
                >
                  {qr.mode !== "ata" && <Check className="w-4 h-4" strokeWidth={2} />} {qr.label}
                </Button>
              )}
              {showVincularExtra && (
                <Button type="button" size="sm" variant="outline" data-testid={`processo-vincular-${p.processo_id}`} onClick={() => a.setVincular(p)} className="border-stone-300 bg-white h-9">
                  Vincular cliente
                </Button>
              )}
            </div>

            <AcompanhamentoProcesso
              processo={p}
              clients={clients}
              onAtualizar={() => a.syncOne(p.processo_id)}
              atualizando={oneBusy === p.processo_id}
              onChanged={a.reload}
            />
          </div>
        )}
      </div>
    </li>
  );
}

